
<template>
    <div
      class="structure-page"
      :class="{ dark: isDarkTheme }"
    >
      <!-- =====================================================
           FIXED HEADER
      ====================================================== -->
  
      <DefaultHeader
        :title="company?.name || 'Штатная структура'"
        subtitle="Штатная структура"
        class="fixed-header"
        :offset="700"
      />
  
      <div class="ambient ambient-one"></div>
      <div class="ambient ambient-two"></div>
  
      <main class="page-container">
  
        <!-- ===================================================
             LOADING
        ==================================================== -->
  
        <div
          v-if="isLoading"
          class="loading-state"
          role="status"
          aria-live="polite"
        >
          <div class="skeleton skeleton-banner"></div>
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-executive"></div>
          <div class="skeleton skeleton-branches"></div>
  
          <div class="skeleton-row">
            <div
              v-for="n in 6"
              :key="n"
              class="skeleton skeleton-avatar"
            ></div>
          </div>
  
          <div class="loading-caption">
            Формируем структуру команды...
          </div>
        </div>
  
        <!-- ===================================================
             ERROR
        ==================================================== -->
  
        <div
          v-else-if="loadError"
          class="error-state"
        >
          <div class="error-symbol">!</div>
  
          <h2>Не удалось загрузить структуру</h2>
  
          <p>{{ loadError }}</p>
  
          <button
            type="button"
            @click="loadCompany"
          >
            Повторить попытку
          </button>
        </div>
  
        <!-- ===================================================
             CONTENT
        ==================================================== -->
  
        <template v-else-if="company">
  
          <!-- =================================================
               COMPACT HERO
          ================================================== -->
  
          <section class="hero">
  
            <div class="hero-decoration hero-orb-one"></div>
            <div class="hero-decoration hero-orb-two"></div>
            <div class="hero-mesh"></div>
  
            <div class="hero-main">
  
              <div class="hero-identity">
  
                <div class="hero-logo">
  
                  <img
                    v-if="company.urlOfLogo && !logoFailed"
                    :src="company.urlOfLogo"
                    alt=""
                    @error="logoFailed = true"
                  />
  
                  <span v-else>
                    {{ companyInitial }}
                  </span>
  
                </div>
  
                <div class="hero-copy">
  
                  <div class="hero-eyebrow">
                    <span class="live-dot"></span>
                    HR3 ORGANIZATION
                  </div>
  
                  <h1>Команда и организация</h1>
  
                  <p>{{ company.name }}</p>
  
                </div>
  
              </div>
  
              <button
                type="button"
                class="hero-refresh"
                :disabled="isLoading"
                title="Обновить структуру"
                aria-label="Обновить структуру"
                @click="loadCompany"
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 11A8 8 0 1 0 17.5 17.2"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                  />
                  <path
                    d="M20 4V11H13"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
  
            </div>
  
            <div class="hero-divider"></div>
  
            <div class="hero-stats">
  
              <div class="hero-stat">
                <strong>{{ statistics.total }}</strong>
                <span>Участников HR3</span>
              </div>
  
              <span class="stat-separator"></span>
  
              <div class="hero-stat">
                <strong>{{ statistics.leaders }}</strong>
                <span>Руководителей</span>
              </div>
  
              <span class="stat-separator"></span>
  
              <div class="hero-stat">
                <strong>{{ statistics.specialists }}</strong>
                <span>Специалистов</span>
              </div>
  
              <span class="stat-separator"></span>
  
              <div class="hero-stat">
                <strong>{{ statistics.current }}</strong>
                <span>Работают сейчас</span>
              </div>
  
            </div>
  
          </section>
  
          <!-- =================================================
               ORGANIZATION CANVAS
          ================================================== -->
  
          <div class="tree-scroll">
  
            <div
              class="org-canvas"
              :style="{
                '--columns': managerColumns,
                '--canvas-width': `${Math.max(1120, managerColumns * 168)}px`
              }"
            >
  
              <!-- =============================================
                   LEVEL 1 — CEO
              ============================================== -->
  
              <section class="tier tier-executive">
  
                <div class="tier-label tier-label-center">
  
                  <span class="tier-index executive-index">
                    01
                  </span>
  
                  <div>
                    <h3>Руководство компании</h3>
                    <p>Стратегия и развитие бизнеса</p>
                  </div>
  
                </div>
  
                <div class="executive-row">
  
                  <StructureCEOComponent
                    v-for="node in executiveNodes"
                    :key="node.key"
                    :employee="node.ghost ? null : node"
                    :dimmed="
                      !node.ghost &&
                      !matchesFilters(node)
                    "
                    @open="openEmployee"
                  />
  
                </div>
  
              </section>
  

<!-- =====================================================
     CONNECTOR LEVEL 1 → LEVEL 2
     PREMIUM CURVED FLOW
====================================================== -->

<div
  class="connector-area connector-executive-manager"
  aria-hidden="true"
>
  <svg
    viewBox="0 0 1000 150"
    preserveAspectRatio="none"
  >
    <defs>

      <!-- GOLD → BLUE GRADIENT -->

      <linearGradient
        id="ceoFlowGradient"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
        gradientUnits="objectBoundingBox"
      >
        <stop
          offset="0%"
          stop-color="#D4AE70"
          stop-opacity="0.95"
        />

        <stop
          offset="42%"
          stop-color="#3989E9"
          stop-opacity="0.8"
        />

        <stop
          offset="100%"
          stop-color="#91BEFA"
          stop-opacity="0.65"
        />
      </linearGradient>

    </defs>

    <!-- CENTRAL TRUNK -->

    <path
      d="M500 0V26"
      class="ceo-flow-trunk"
    />

    <!-- GLOW AROUND ROOT -->

    <circle
      cx="500"
      cy="26"
      r="15"
      class="ceo-flow-halo"
    />

    <!-- ROOT NODE -->

    <circle
      cx="500"
      cy="26"
      r="7"
      class="ceo-flow-root"
    />

    <circle
      cx="500"
      cy="26"
      r="2.5"
      class="ceo-flow-root-center"
    />

    <!-- SIX CURVED BRANCHES -->

    <g
      v-for="i in managerColumns"
      :key="`ceo-flow-${i}`"
    >

      <!-- BASE LINE -->

      <path
        :d="managerFanPath(i - 1)"
        class="ceo-flow-branch"
      />

      <!-- MOVING LIGHT -->

      <path
        :d="managerFanPath(i - 1)"
        class="ceo-flow-particles"
        :style="{
          animationDelay: `${(i - 1) * -0.45}s`
        }"
      />

      <!-- ARROWHEAD -->

      <path
        :d="`
          M ${columnX(i - 1) - 6} 130
          L ${columnX(i - 1)} 140
          L ${columnX(i - 1) + 6} 130
        `"
        class="ceo-flow-arrow"
      />

    </g>

  </svg>
</div>

  
              <!-- =============================================
                   LEVEL 2 — MANAGEMENT
              ============================================== -->
  
              <section class="tier tier-managers">
  
                <!-- GROUP TITLE -->
  
                <div class="tier-label">
  
                  <span class="tier-index manager-index">
                    02
                  </span>
  
                  <div>
                    <h3>Руководители направлений</h3>
                    <p>Лидеры команд и управленческие роли</p>
                  </div>
  
                  <span class="tier-real-count">
                    Профилей в HR3:
                    {{ statistics.leaders }}
                  </span>
  
                </div>
  
                <!-- ===========================================
                     MANAGEMENT DEPTH STAGE
                ============================================ -->
  
                <div class="manager-depth-stage">
  
                  <!-- =========================================
                       BACK LAYER — SIX GHOSTS
                       Decorative only
                  ========================================== -->
  
                  <div
                    class="node-grid manager-back-layer"
                    aria-hidden="true"
                  >
  
                    <UnlockCharachterComponent
                      v-for="(ghost, index) in managerBackgroundSlots"
                      :key="ghost.key"
                      :level="2"
                      :tone="(index + 2) % 4"
                      class="manager-depth-ghost"
                    />
  
                  </div>
  
                  <!-- =========================================
                       FRONT LAYER — SIX CHARACTERS
                  ========================================== -->
  
                  <div class="node-grid manager-front-layer">
  
                    <template
                      v-for="(node, index) in managerSlots"
                      :key="node.key"
                    >
  
                      <!-- UNKNOWN MANAGER -->
  
                      <UnlockCharachterComponent
                        v-if="node.ghost"
                        :level="2"
                        :tone="index % 4"
                        class="structure-node-slot"
                        :style="{
                          '--node-delay': `${Math.min(index, 12) * 45}ms`
                        }"
                      />
  
                      <!-- REAL MANAGER -->
  
                      <StructureWorkerComponent
                        v-else
                        :employee="node"
                        :level="2"
                        :dimmed="!matchesFilters(node)"
                        class="structure-node-slot"
                        :style="{
                          '--node-delay': `${Math.min(index, 12) * 45}ms`
                        }"
                        @open="openEmployee"
                      />
  
                    </template>
  
                  </div>
  
                </div>
  
                <!-- ===========================================
                     ADDITIONAL REAL MANAGERS
                     If more than six exist
                ============================================ -->
  
                <div
                  v-for="(row, rowIndex) in extraManagerRows"
                  :key="`manager-extra-row-${rowIndex}`"
                  class="manager-overflow-section"
                >
  
                  <div
                    class="connector-area connector-manager-extra"
                    aria-hidden="true"
                  >
  
                    <svg
                      viewBox="0 0 1000 68"
                      preserveAspectRatio="none"
                    >
  
                      <line
                        :x1="columnX(0)"
                        y1="32"
                        :x2="columnX(managerColumns - 1)"
                        y2="32"
                        class="connection-main"
                      />
  
                      <g
                        v-for="i in managerColumns"
                        :key="`extra-manager-${rowIndex}-${i}`"
                      >
  
                        <line
                          :x1="columnX(i - 1)"
                          y1="0"
                          :x2="columnX(i - 1)"
                          y2="60"
                          class="connection-stem"
                        />
  
                        <circle
                          :cx="columnX(i - 1)"
                          cy="32"
                          r="3.3"
                          class="branch-junction"
                        />
  
                      </g>
  
                    </svg>
  
                  </div>
  
                  <div class="node-grid manager-overflow-grid">
  
                    <template
                      v-for="(node, index) in row"
                      :key="node.key"
                    >
  
                      <UnlockCharachterComponent
                        v-if="node.ghost"
                        :level="2"
                        :tone="(index + rowIndex + 1) % 4"
                        class="structure-node-slot"
                      />
  
                      <StructureWorkerComponent
                        v-else
                        :employee="node"
                        :level="2"
                        :dimmed="!matchesFilters(node)"
                        class="structure-node-slot"
                        @open="openEmployee"
                      />
  
                    </template>
  
                  </div>
  
                </div>
  
              </section>
  
              <!-- =============================================
                   CONNECTOR 2 → 3
              ============================================== -->
  
              <div
                class="connector-area connector-manager-staff"
                aria-hidden="true"
              >
  
                <svg
                  viewBox="0 0 1000 136"
                  preserveAspectRatio="none"
                >
  
                  <!-- SIX MANAGEMENT ROOTS -->
  
                  <line
                    v-for="i in managerColumns"
                    :key="`management-root-${i}`"
                    :x1="columnX(i - 1)"
                    y1="0"
                    :x2="columnX(i - 1)"
                    y2="43"
                    class="connection-stem"
                  />
  
                  <!-- UPPER HORIZONTAL LINE -->
  
                  <line
                    :x1="columnX(0)"
                    y1="43"
                    :x2="columnX(managerColumns - 1)"
                    y2="43"
                    class="connection-main"
                  />
  
                  <!-- CENTRAL CONNECTION -->
  
                  <line
                    x1="500"
                    y1="43"
                    x2="500"
                    y2="75"
                    class="connection-main"
                  />
  
                  <circle
                    cx="500"
                    cy="75"
                    r="6"
                    class="connector-center"
                  />
  
                  <!-- LOWER HORIZONTAL LINE -->
  
                  <line
                    :x1="columnX(0)"
                    y1="88"
                    :x2="columnX(managerColumns - 1)"
                    y2="88"
                    class="connection-main"
                  />
  
                  <line
                    x1="500"
                    y1="75"
                    x2="500"
                    y2="88"
                    class="connection-main"
                  />
  
                  <!-- STAFF BRANCHES -->
  
                  <g
                    v-for="i in managerColumns"
                    :key="`staff-branch-${i}`"
                  >
  
                    <line
                      :x1="columnX(i - 1)"
                      y1="88"
                      :x2="columnX(i - 1)"
                      y2="123"
                      class="connection-stem"
                      :class="{
                        'connection-highlight': i % 2 === 0
                      }"
                    />
  
                    <circle
                      :cx="columnX(i - 1)"
                      cy="88"
                      r="3"
                      class="branch-junction"
                    />
  
                    <circle
                      :cx="columnX(i - 1)"
                      cy="124"
                      r="4"
                      class="branch-end"
                    />
  
                  </g>
  
                </svg>
  
              </div>
  
              <!-- =============================================
                   LEVEL 3 — STAFF
              ============================================== -->
  
              <section class="tier tier-staff">
  
                <!-- GROUP TITLE -->
  
                <div class="tier-label">
  
                  <span class="tier-index staff-index">
                    03
                  </span>
  
                  <div>
                    <h3>Команда специалистов</h3>
  
                    <p>
                      Люди, которые создают продукт
                      и развивают компанию
                    </p>
                  </div>
  
                  <span class="tier-real-count">
                    Профилей в HR3:
                    {{ statistics.specialists }}
                  </span>
  
                </div>
  
                <!-- STAFF ROWS -->
  
                <div class="staff-rows">
  
                  <div
                    v-for="(row, rowIndex) in staffRows"
                    :key="`staff-row-${rowIndex}`"
                    class="staff-row-block"
                  >
  
                    <!-- EXTRA CONNECTOR -->
  
                    <div
                      v-if="rowIndex > 0"
                      class="connector-area connector-extra"
                      aria-hidden="true"
                    >
  
                      <svg
                        viewBox="0 0 1000 83"
                        preserveAspectRatio="none"
                      >
  
                        <line
                          :x1="columnX(0)"
                          y1="35"
                          :x2="columnX(managerColumns - 1)"
                          y2="35"
                          class="connection-main"
                        />
  
                        <g
                          v-for="i in managerColumns"
                          :key="`extra-${rowIndex}-${i}`"
                        >
  
                          <line
                            :x1="columnX(i - 1)"
                            y1="0"
                            :x2="columnX(i - 1)"
                            y2="70"
                            class="connection-stem"
                          />
  
                          <circle
                            :cx="columnX(i - 1)"
                            cy="35"
                            r="3.5"
                            class="branch-junction"
                          />
  
                          <circle
                            :cx="columnX(i - 1)"
                            cy="70"
                            r="4"
                            class="branch-end"
                          />
  
                        </g>
  
                      </svg>
  
                    </div>
  
                    <!-- STAFF CARDS -->
  
                    <div class="node-grid staff-grid">
  
                      <template
                        v-for="(node, index) in row"
                        :key="node.key"
                      >
  
                        <!-- UNKNOWN SPECIALIST -->
  
                        <UnlockCharachterComponent
                          v-if="node.ghost"
                          :level="3"
                          :tone="(index + rowIndex) % 4"
                          class="structure-node-slot"
                          :style="{
                            '--node-delay': `${Math.min(index, 12) * 30}ms`
                          }"
                        />
  
                        <!-- REAL SPECIALIST -->
  
                        <StructureWorkerComponent
                          v-else
                          :employee="node"
                          :level="3"
                          :dimmed="!matchesFilters(node)"
                          class="structure-node-slot"
                          :style="{
                            '--node-delay': `${Math.min(index, 12) * 30}ms`
                          }"
                          @open="openEmployee"
                        />
  
                      </template>
  
                    </div>
  
                  </div>
  
                </div>
  
              </section>
  
              <!-- =============================================
                   END OF TREE
              ============================================== -->
  
              <div class="tree-ending">
  
                <div class="ending-line"></div>
                <div class="ending-dot"></div>
  
                <span>
                  КАЖДЫЙ ЧЕЛОВЕК — ЧАСТЬ БОЛЬШОЙ КОМАНДЫ
                </span>
  
              </div>
  
            </div>
  
          </div>
  
          <!-- =================================================
               FOOTNOTE
          ================================================== -->
  
          <div class="structure-note">
  
            <div class="note-icon">
  
              <svg viewBox="0 0 24 24" fill="none">
  
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
  
                <path
                  d="M12 11V16M12 8H12.01"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
  
              </svg>
  
            </div>
  
            <p>
              Силуэты со знаком вопроса обозначают участников,
              которые пока не представлены в HR3.
              Они не являются подтверждёнными штатными
              единицами или вакансиями.
  
              Соединительные линии показывают уровни ролей,
              а не фактическое подчинение сотрудников.
            </p>
  
          </div>
  
        </template>
  
      </main>
  
      <!-- =====================================================
           FIXED FOOTER
      ====================================================== -->
  
      <Footer class="fixed-footer" />
  
    </div>
  </template>
  
  <script setup>
  import {
    ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount
  } from "vue";
  
  import {
    useRoute,
    useRouter
  } from "vue-router";
  
  import DefaultHeader from "../components/CompanyHeader.vue";
  import Footer from "../components/MiniFooter.vue";
  
  import CompanyService from "../api/CompanyService.js";
  
  import StructureCEOComponent
    from "../components/StructureCEOComponent.vue";
  
  import UnlockCharachterComponent
    from "../components/UnlockCharachterComponent.vue";
  
  import StructureWorkerComponent
    from "../components/StructureWorkerComponent.vue";
  
  // ============================================================
  // ROUTER
  // ============================================================
  
  const route = useRoute();
  const router = useRouter();
  
  const companyId = computed(() => {
    const value =
      route.params.companyId ??
      route.query.companyId ??
      "";
  
    return typeof value === "string"
      ? value.trim()
      : "";
  });
  
  // ============================================================
  // STATE
  // ============================================================
  
  const company = ref(null);
  
  const isLoading = ref(true);
  const loadError = ref("");
  
  const searchQuery = ref("");
  const activeFilter = ref("all");
  
  const isDarkTheme = ref(false);
  const logoFailed = ref(false);
  
  let themeObserver = null;
  let requestCounter = 0;
  
  // ============================================================
  // DESIGN CONFIGURATION
  // ============================================================
  
  // Six main positions at management level.
  const MANAGERS_PER_ROW = 6;
  
  // Six decorative background characters.
  const MANAGER_BACK_ROW = 6;
  
  // At least eighteen specialist slots.
  const MIN_STAFF = 18;
  
  // ============================================================
  // THEME
  // ============================================================
  
  function updateTheme() {
    isDarkTheme.value =
      document.documentElement.getAttribute(
        "data-theme"
      ) === "dark";
  }
  
  // ============================================================
  // FETCH COMPANY WITH STRUCTURE
  // ============================================================
  
  async function loadCompany() {
    const currentRequest = ++requestCounter;
  
    isLoading.value = true;
    loadError.value = "";
  
    try {
      if (!companyId.value) {
        throw new Error("Не указан ID компании");
      }
  
      const result =
        await CompanyService.getCompanyWithStructure(
          companyId.value
        );
  
      if (!result) {
        throw new Error("Компания не найдена");
      }
  
      if (currentRequest !== requestCounter) {
        return;
      }
  
      company.value = result;
      logoFailed.value = false;
  
      console.log(
        "🏢 Штатная структура загружена:",
        result
      );
  
    } catch (error) {
      if (currentRequest !== requestCounter) {
        return;
      }
  
      company.value = null;
  
      loadError.value =
        error?.message ||
        "Не удалось загрузить штатную структуру";
  
      console.error(
        "❌ Ошибка загрузки структуры:",
        error
      );
  
    } finally {
      if (currentRequest === requestCounter) {
        isLoading.value = false;
      }
    }
  }
  
  // ============================================================
  // EMPLOYEES
  // ============================================================
  
  const employees = computed(() => {
    const value = company.value?.employees;
  
    return Array.isArray(value)
      ? value
      : [];
  });
  
  // ============================================================
  // EMPLOYEES BY LEVEL
  // ============================================================
  
  const executives = computed(() =>
    employees.value.filter(
      employee => employee.level === 1
    )
  );
  
  const managers = computed(() =>
    employees.value.filter(
      employee => employee.level === 2
    )
  );
  
  const specialists = computed(() =>
    employees.value.filter(
      employee => employee.level === 3
    )
  );
  
  // ============================================================
  // STATISTICS
  // ============================================================
  
  const statistics = computed(() => {
    const list = employees.value;
  
    return {
      total: list.length,
  
      executives: executives.value.length,
      leaders: managers.value.length,
      specialists: specialists.value.length,
  
      current: list.filter(
        employee =>
          employee.isCurrentEmployee === true
      ).length,
  
      former: list.filter(
        employee =>
          employee.isCurrentEmployee === false
      ).length,
  
      unknown: list.filter(
        employee =>
          employee.isCurrentEmployee == null
      ).length
    };
  });
  
  // ============================================================
  // COMPANY INITIAL
  // ============================================================
  
  const companyInitial = computed(() => {
    const name = String(
      company.value?.name || "H"
    ).trim();
  
    return name.charAt(0).toUpperCase() || "H";
  });
  
  // ============================================================
  // GHOST FACTORY
  // ============================================================
  
  function createGhost(level, index) {
    return {
      key: `ghost-${level}-${index}`,
      ghost: true,
  
      level,
  
      userId: null,
      relationId: null,
  
      name: null,
      specifics: null,
      imageURL: null,
  
      isCurrentEmployee: null
    };
  }
  
  // ============================================================
  // REAL NODE FACTORY
  // ============================================================
  
  function asRealNode(employee, index) {
    return {
      ...employee,
  
      key:
        employee.relationId ||
        `${employee.userId}-${employee.level}-${index}`,
  
      ghost: false
    };
  }
  
  // ============================================================
  // LEVEL 1 — CEO
  // ============================================================
  
  const executiveNodes = computed(() => {
    if (executives.value.length > 0) {
      return executives.value.map(asRealNode);
    }
  
    return [
      createGhost(1, 0)
    ];
  });
  
  // ============================================================
  // LEVEL 2 — SIX COLUMNS
  // ============================================================
  
  // Fixed geometry for the second and third levels.
  const managerColumns = MANAGERS_PER_ROW;
  
  // ============================================================
  // LEVEL 2 — BACKGROUND CHARACTERS
  // ============================================================
  
  // Decorative characters only.
  // They never enter employees or statistics.
  
  const managerBackgroundSlots = Array.from(
    { length: MANAGER_BACK_ROW },
    (_, index) =>
      createGhost(2, `background-${index}`)
  );
  
  // ============================================================
  // LEVEL 2 — FRONT CHARACTERS
  // ============================================================
  
  // For one or two managers, center the real profiles.
  // The visual order is:
  //        2, 3, 1, 4, 0, 5
  
  const managerSlots = computed(() => {
    const front = Array.from(
      { length: MANAGERS_PER_ROW },
      (_, index) =>
        createGhost(2, `front-${index}`)
    );
  
    const centerFirst = [
      2, 3, 1, 4, 0, 5
    ];
  
    managers.value
      .slice(0, MANAGERS_PER_ROW)
      .map(asRealNode)
      .forEach((person, index) => {
        front[centerFirst[index]] = person;
      });
  
    return front;
  });
  
  // ============================================================
  // LEVEL 2 — ADDITIONAL MANAGERS
  // ============================================================
  
  // If there are more than six real managers,
  // we preserve everyone using extra rows.
  
  const extraManagerRows = computed(() => {
    const extra = managers.value
      .slice(MANAGERS_PER_ROW)
      .map(
        (person, index) =>
          asRealNode(
            person,
            index + MANAGERS_PER_ROW
          )
      );
  
    const rows = [];
  
    for (
      let index = 0;
      index < extra.length;
      index += MANAGERS_PER_ROW
    ) {
      const row = extra.slice(
        index,
        index + MANAGERS_PER_ROW
      );
  
      while (row.length < MANAGERS_PER_ROW) {
        row.push(
          createGhost(
            2,
            `overflow-${index}-${row.length}`
          )
        );
      }
  
      rows.push(row);
    }
  
    return rows;
  });
  
  // ============================================================
  // LEVEL 3 — SPECIALIST SLOTS
  // ============================================================
  
  const staffSlots = computed(() => {
    const realNodes =
      specialists.value.map(asRealNode);
  
    const columns = managerColumns;
  
    const targetCount =
      Math.ceil(
        Math.max(
          MIN_STAFF,
          realNodes.length
        ) / columns
      ) * columns;
  
    const slots = [...realNodes];
  
    while (slots.length < targetCount) {
      slots.push(
        createGhost(3, slots.length)
      );
    }
  
    return slots;
  });
  
  // ============================================================
  // LEVEL 3 — SPECIALIST ROWS
  // ============================================================
  
  const staffRows = computed(() => {
    const rows = [];
  
    const columns = managerColumns;
  
    for (
      let index = 0;
      index < staffSlots.value.length;
      index += columns
    ) {
      rows.push(
        staffSlots.value.slice(
          index,
          index + columns
        )
      );
    }
  
    return rows;
  });
  
  // ============================================================
  // CONNECTOR GEOMETRY
  // ============================================================
  
  // SVG viewBox = 1000.
  // Every branch matches the center of a grid column.
  
  function columnX(index) {
    return (
      (index + 0.5) *
      (1000 / managerColumns)
    );
  }
  
  // ============================================================
  // SEARCH FILTERS
  // ============================================================
  
  const statusFilters = computed(() => [
    {
      value: "all",
      label: "Все",
      count: statistics.value.total
    },
    {
      value: "current",
      label: "Работают",
      count: statistics.value.current
    },
    {
      value: "former",
      label: "Работали",
      count: statistics.value.former
    },
    {
      value: "unknown",
      label: "Не указан",
      count: statistics.value.unknown
    }
  ]);
  
  const hasActiveFilters = computed(() => {
    return (
      searchQuery.value.trim().length > 0 ||
      activeFilter.value !== "all"
    );
  });
  
  // ============================================================
  // MATCH EMPLOYEE AGAINST FILTERS
  // ============================================================
  
  // We dim unmatched real profiles instead of
  // removing them from the organization tree.
  
  function matchesFilters(employee) {
    if (!employee || employee.ghost) {
      return false;
    }
  
    const search = searchQuery.value
      .trim()
      .toLocaleLowerCase();
  
    const name = String(
      employee.name || ""
    ).toLocaleLowerCase();
  
    const specialty = String(
      employee.specifics || ""
    ).toLocaleLowerCase();
  
    const matchesSearch =
      !search ||
      name.includes(search) ||
      specialty.includes(search);
  
    let matchesStatus = true;
  
    if (activeFilter.value === "current") {
      matchesStatus =
        employee.isCurrentEmployee === true;
    }
  
    if (activeFilter.value === "former") {
      matchesStatus =
        employee.isCurrentEmployee === false;
    }
  
    if (activeFilter.value === "unknown") {
      matchesStatus =
        employee.isCurrentEmployee == null;
    }
  
    return matchesSearch && matchesStatus;
  }
  
  // ============================================================
  // MATCHED EMPLOYEES
  // ============================================================
  
  const matchedEmployees = computed(() =>
    employees.value.filter(matchesFilters)
  );
  
  // ============================================================
  // RESET FILTERS
  // ============================================================
  
  function resetFilters() {
    searchQuery.value = "";
    activeFilter.value = "all";
  }
  
  // ============================================================
  // OPEN EMPLOYEE PROFILE
  // ============================================================
  
  function openEmployee(employee) {
    if (
      !employee ||
      employee.ghost ||
      !employee.userId
    ) {
      return;
    }
  
    router.push({
      name: "User",
      params: {
        id: employee.userId
      }
    });
  }
  
  // ============================================================
  // WATCH COMPANY ID
  // ============================================================
  
  watch(
    companyId,
    () => {
      resetFilters();
      loadCompany();
    },
    {
      immediate: true
    }
  );
  
  // ============================================================
  // LIFECYCLE
  // ============================================================
  
  onMounted(() => {
    updateTheme();
  
    themeObserver = new MutationObserver(
      updateTheme
    );
  
    themeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: ["data-theme"]
      }
    );
  });
  
  onBeforeUnmount(() => {
    requestCounter += 1;
  
    themeObserver?.disconnect();
    themeObserver = null;
  });
  
  
function managerFanPath(index) {
  const x = columnX(index);

  return `
    M 500 26
    C 500 68,
      ${x} 62,
      ${x} 140
  `;
}

  
  </script>

  
  <style scoped>
  
  /* ============================================================
     PAGE VARIABLES
  ============================================================ */
  
  .structure-page {
    --page-bg: #f5f7fa;
    --surface: #ffffff;
    --surface-soft: #f8faff;
    --text: #243247;
    --muted: #8896aa;
    --subtle: #a3aec0;
    --border: rgba(40, 78, 134, 0.095);
    --blue: #2173e5;
    --blue-soft: rgba(33, 115, 229, 0.08);
    --shadow: 0 13px 37px rgba(29, 64, 113, 0.05);
  
    min-height: 100vh;
    position: relative;
    overflow: hidden;
  
    background: var(--page-bg);
    color: var(--text);
  
    font-family: "Eastman-Regular", sans-serif;
  
    transition: background 0.25s ease;
  }
  
  .structure-page.dark {
    --page-bg: #15171b;
    --surface: #202329;
    --surface-soft: #272c34;
    --text: #f1f3f7;
    --muted: #a0adbe;
    --subtle: #77869b;
    --border: rgba(255, 255, 255, 0.085);
    --blue: #8cbbff;
    --blue-soft: rgba(83, 145, 240, 0.13);
    --shadow: 0 13px 37px rgba(0, 0, 0, 0.18);
  }
  
  .structure-page *,
  .structure-page *::before,
  .structure-page *::after {
    box-sizing: border-box;
  }
  
  /* ============================================================
     FIXED HEADER AND FOOTER
  ============================================================ */
  
  .fixed-header {
    position: fixed !important;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
  }
  
  .fixed-footer {
    position: fixed !important;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
  }
  
  /* ============================================================
     BACKGROUND
  ============================================================ */
  
  .ambient {
    position: absolute;
    pointer-events: none;
    border-radius: 50%;
    filter: blur(90px);
  }
  
  .ambient-one {
    width: 500px;
    height: 500px;
    top: 250px;
    right: -300px;
    background: rgba(54, 127, 236, 0.08);
  }
  
  .ambient-two {
    width: 450px;
    height: 450px;
    top: 1100px;
    left: -270px;
    background: rgba(81, 120, 218, 0.065);
  }
  
  /* ============================================================
     PAGE CONTAINER
  ============================================================ */
  
  .page-container {
    position: relative;
    z-index: 1;
  
    width: min(1220px, calc(100% - 40px));
    margin: 0 auto;
  
    padding: 108px 0 135px;
  }
  
  /* ============================================================
     HERO
  ============================================================ */
  
  .hero {
    position: relative;
    overflow: hidden;
  
    padding: 27px 32px 23px;
  
    border-radius: 22px;
  
    background:
      linear-gradient(
        112deg,
        #12376e 0%,
        #1c62ca 57%,
        #3789ef 100%
      );
  
    color: #ffffff;
  
    box-shadow:
      0 17px 39px rgba(24, 95, 191, 0.15);
  }
  
  .hero-main {
    position: relative;
    z-index: 2;
  
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  
    gap: 20px;
  }
  
  .hero-identity {
    display: flex;
    align-items: center;
  
    gap: 17px;
    min-width: 0;
  }
  
  .hero-logo {
    width: 67px;
    height: 67px;
  
    flex-shrink: 0;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    overflow: hidden;
  
    background: #ffffff;
    color: #226bd1;
  
    border-radius: 18px;
    border: 3px solid rgba(255, 255, 255, 0.25);
  
    box-shadow:
      0 9px 22px rgba(0, 0, 0, 0.12);
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 26px;
  }
  
  .hero-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  .hero-copy {
    min-width: 0;
  }
  
  .hero-eyebrow {
    display: flex;
    align-items: center;
  
    gap: 9px;
  
    color: rgba(255, 255, 255, 0.70);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 9px;
    letter-spacing: 1.5px;
  }
  
  .live-dot {
    width: 6px;
    height: 6px;
    flex-shrink: 0;
  
    border-radius: 50%;
  
    background: #b8e0ff;
  
    box-shadow:
      0 0 0 5px rgba(180, 220, 255, 0.12);
  }
  
  .hero-copy h1 {
    margin: 8px 0 0;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: clamp(20px, 2.4vw, 28px);
  
    line-height: 1.25;
    letter-spacing: -0.5px;
  }
  
  .hero-copy p {
    margin: 6px 0 0;
  
    color: rgba(255, 255, 255, 0.75);
  
    font-size: 12px;
    line-height: 1.45;
  
    overflow-wrap: anywhere;
  }
  
  .hero-refresh {
    width: 37px;
    height: 37px;
  
    flex-shrink: 0;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    padding: 0;
  
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
  
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 11px;
  
    cursor: pointer;
  
    transition:
      background 0.2s ease,
      transform 0.2s ease;
  }
  
  .hero-refresh:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.23);
    transform: rotate(20deg);
  }
  
  .hero-refresh:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .hero-refresh svg {
    width: 18px;
    height: 18px;
  }
  
  .hero-divider {
    position: relative;
    z-index: 2;
  
    height: 1px;
    margin: 23px 0 20px;
  
    background: rgba(255, 255, 255, 0.17);
  }
  
  .hero-stats {
    position: relative;
    z-index: 2;
  
    display: flex;
    align-items: center;
    gap: 29px;
  }
  
  .hero-stat {
    min-width: 90px;
  
    display: flex;
    flex-direction: column;
  
    gap: 5px;
  }
  
  .hero-stat strong {
    font-family: "Eastman-Bold", sans-serif;
    font-size: 25px;
    line-height: 1;
  }
  
  .hero-stat > span {
    color: rgba(255, 255, 255, 0.72);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 9px;
  }
  
  .stat-separator {
    width: 1px;
    height: 30px;
    background: rgba(255, 255, 255, 0.20);
  }
  
  .hero-decoration {
    position: absolute;
    pointer-events: none;
    border-radius: 50%;
  }
  
  .hero-orb-one {
    width: 330px;
    height: 330px;
  
    right: -120px;
    top: -230px;
  
    background: rgba(255, 255, 255, 0.08);
  
    box-shadow:
      0 0 0 65px rgba(255, 255, 255, 0.035),
      0 0 0 130px rgba(255, 255, 255, 0.015);
  }
  
  .hero-orb-two {
    width: 230px;
    height: 230px;
  
    right: 170px;
    bottom: -210px;
  
    background: rgba(165, 215, 255, 0.1);
  }
  
  .hero-mesh {
    position: absolute;
    inset: 0;
  
    background-image:
      linear-gradient(
        rgba(255, 255, 255, 0.045) 1px,
        transparent 1px
      ),
      linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.045) 1px,
        transparent 1px
      );
  
    background-size: 37px 37px;
  
    mask-image:
      linear-gradient(to left, #000, transparent 85%);
  }
  
  /* ============================================================
     INTRO
  ============================================================ */
  
  .section-intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  
    gap: 20px;
    margin-top: 39px;
  }
  
  .section-eyebrow {
    display: flex;
    align-items: center;
  
    gap: 8px;
  
    color: var(--blue);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 9px;
    letter-spacing: 1.2px;
  }
  
  .section-eyebrow span {
    width: 17px;
    height: 2px;
    background: var(--blue);
  }
  
  .section-intro h2 {
    margin: 11px 0 0;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 24px;
  
    letter-spacing: -0.5px;
    line-height: 1.3;
  }
  
  .section-intro h2 em {
    font-style: normal;
    color: var(--blue);
  }
  
  .section-intro p {
    max-width: 580px;
    margin: 8px 0 0;
  
    color: var(--muted);
  
    font-size: 11px;
    line-height: 1.7;
  }
  
  .legend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  
    gap: 15px;
    padding-bottom: 4px;
  }
  
  .legend-item {
    display: flex;
    align-items: center;
  
    gap: 7px;
  
    color: var(--muted);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 10px;
  
    white-space: nowrap;
  }
  
  .legend-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
  
  .real-dot {
    background: #2d88eb;
  
    box-shadow:
      0 0 0 4px rgba(45, 136, 235, 0.09);
  }
  
  .hidden-dot {
    background: #b6c4d7;
  }
  
  /* ============================================================
     TOOLBAR
  ============================================================ */
  
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  
    gap: 13px;
  
    margin-top: 23px;
    padding: 12px;
  
    background: var(--surface);
  
    border: 1px solid var(--border);
    border-radius: 15px;
  
    box-shadow: var(--shadow);
  }
  
  .search-box {
    display: flex;
    align-items: center;
  
    gap: 9px;
  
    flex: 1;
    min-width: 260px;
  
    height: 38px;
    padding: 0 12px;
  
    color: var(--muted);
    background: var(--surface-soft);
  
    border: 1px solid var(--border);
    border-radius: 9px;
  }
  
  .search-box svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  .search-box input {
    width: 100%;
    min-width: 0;
  
    border: 0;
    outline: none;
  
    background: transparent;
    color: var(--text);
  
    font-family: "Eastman-Regular", sans-serif;
    font-size: 11px;
  }
  
  .search-box input::placeholder {
    color: var(--subtle);
  }
  
  .clear-button {
    padding: 2px 5px;
    border: none;
  
    background: transparent;
    color: var(--muted);
  
    font-size: 19px;
    cursor: pointer;
  }
  
  .filter-tabs {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  
    gap: 4px;
  }
  
  .filter-tab {
    min-height: 32px;
  
    display: flex;
    align-items: center;
  
    gap: 6px;
    padding: 0 10px;
  
    border: none;
    border-radius: 8px;
  
    background: transparent;
    color: var(--muted);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 10px;
  
    cursor: pointer;
  }
  
  .filter-tab.active {
    background: #2173e5;
    color: #ffffff;
  }
  
  .filter-tab:not(.active):hover {
    background: var(--blue-soft);
    color: var(--blue);
  }
  
  .filter-tab span {
    font-size: 9px;
  
    padding: 2px 5px;
    border-radius: 5px;
  
    background: var(--surface-soft);
    color: var(--muted);
  }
  
  .filter-tab.active span {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }
  
  .filter-feedback {
    display: flex;
    align-items: center;
    justify-content: space-between;
  
    gap: 10px;
    margin-top: 12px;
  
    color: var(--muted);
    font-size: 11px;
  }
  
  .filter-feedback button {
    border: 0;
  
    background: none;
    color: var(--blue);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 10px;
  
    cursor: pointer;
  }
  
  /* ============================================================
     TREE HEADER
  ============================================================ */
  
  .tree-heading {
    display: flex;
    align-items: center;
  
    gap: 12px;
  
    margin-top: 39px;
    padding-bottom: 16px;
  
    border-bottom: 1px solid var(--border);
  }
  
  .tree-heading-icon {
    width: 41px;
    height: 41px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    flex-shrink: 0;
  
    border-radius: 12px;
  
    background: var(--blue-soft);
    color: var(--blue);
  }
  
  .tree-heading-icon svg {
    width: 21px;
    height: 21px;
  }
  
  .tree-heading-content {
    min-width: 0;
  }
  
  .tree-heading h3 {
    margin: 0;
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 14px;
  }
  
  .tree-heading p {
    margin: 4px 0 0;
  
    color: var(--muted);
    font-size: 10px;
  }
  
  .tree-version {
    margin-left: auto;
    flex-shrink: 0;
  
    color: var(--subtle);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 10px;
    letter-spacing: 1px;
  }
  
  .mobile-scroll-hint {
    display: none;
  }
  
  /* ============================================================
     TREE CANVAS
  ============================================================ */
  
  .tree-scroll {
    margin-top: 16px;
  
    overflow-x: auto;
    overflow-y: hidden;
  
    padding: 14px 0 24px;
  
    scrollbar-color:
      rgba(72, 128, 211, 0.23)
      transparent;
  
    scrollbar-width: thin;
  }
  
  .org-canvas {
    width: 100%;
    min-width: var(--canvas-width);
  
    padding: 18px 15px 8px;
  
    overflow: hidden;
  
    background:
      radial-gradient(
        ellipse at 50% 8%,
        rgba(60, 126, 224, 0.045),
        transparent 52%
      );
  }
  
  /* ============================================================
     TIER BASE
  ============================================================ */
  
  .tier {
    position: relative;
  }
  
  .tier-label {
    display: flex;
    align-items: center;
  
    gap: 10px;
    margin-bottom: 25px;
  }
  
  .tier-label-center {
    justify-content: center;
  }
  
  .tier-index {
    width: 36px;
    height: 36px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    flex-shrink: 0;
  
    border-radius: 11px;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 13px;
  }
  
  .executive-index {
    background: rgba(218, 167, 79, 0.11);
    color: #c59a50;
  }
  
  .manager-index {
    background: rgba(33, 115, 229, 0.10);
    color: var(--blue);
  }
  
  .staff-index {
    background: rgba(57, 161, 142, 0.11);
    color: #4baf9c;
  }
  
  .tier-label h3 {
    margin: 0;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 14px;
  }
  
  .tier-label p {
    margin: 4px 0 0;
  
    font-size: 10px;
    color: var(--muted);
  }
  
  .tier-real-count {
    margin-left: auto;
  
    padding: 7px 10px;
  
    background: var(--surface);
    color: var(--muted);
  
    border: 1px solid var(--border);
    border-radius: 8px;
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 9px;
  }
  
  /* ============================================================
     CEO
  ============================================================ */
  
  .executive-row {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  
    gap: 20px;
  }
  
  /* ============================================================
     MANAGEMENT DEPTH EFFECT
     SIX FRONT + SIX BACK
  ============================================================ */
  
  .manager-depth-stage {
    position: relative;
    isolation: isolate;
  
    /* Reveal the back row above the front cards. */
    padding-top: 76px;
  
    perspective: 1200px;
  }
  
  /* ============================================================
     BACK ROW
  ============================================================ */
  
  .manager-back-layer {
    position: absolute;
  
    top: 0;
    left: 0;
    right: 0;
  
    z-index: 0;
  
    pointer-events: none;
  
    filter: blur(0.65px) saturate(0.75);
  }
  
  /*
    These are ordinary UnlockCharachterComponent instances,
    but styled as distant decorative characters.
  */
  
  .manager-back-layer .manager-depth-ghost {
  opacity: 0.30;

  transform:
    translateY(-2px)
    scale(0.72)
    rotateX(7deg);

  transform-origin: top center;

  box-shadow:
    0 18px 33px rgba(45, 109, 196, 0.055);

  animation: none;
  transition: none;
}

.manager-back-layer .manager-depth-ghost:nth-child(odd) {
  transform:
    translate(-7px, -2px)
    scale(0.72)
    rotateX(7deg);
}

.manager-back-layer .manager-depth-ghost:nth-child(even) {
  transform:
    translate(7px, -2px)
    scale(0.72)
    rotateX(7deg);
}
  /* ============================================================
     FRONT ROW
  ============================================================ */
  
  .manager-front-layer {
    position: relative;
    z-index: 2;
  }
  
  /* Soft visual separator between layers. */
  
  .manager-depth-stage::after {
    content: "";
  
    position: absolute;
    z-index: 1;
  
    top: 65px;
    left: 0;
    right: 0;
  
    height: 45px;
  
    pointer-events: none;
  
    background:
      linear-gradient(
        to bottom,
        transparent,
        var(--page-bg)
      );
  
    opacity: 0.27;
  }
  
  /* ============================================================
     EXTRA MANAGERS
  ============================================================ */
  
  .manager-overflow-section {
    position: relative;
    margin-top: 10px;
  }
  
  .connector-manager-extra {
    height: 68px;
  }
  
  .manager-overflow-grid {
    position: relative;
    z-index: 2;
  }
  
  /* ============================================================
     DARK MANAGEMENT DEPTH
  ============================================================ */
  
  .structure-page.dark .manager-back-layer {
    filter: blur(0.65px) saturate(0.65);
  }
  
  .structure-page.dark .manager-back-layer .manager-depth-ghost {
    opacity: 0.4;
  }
  
  /* ============================================================
     CONNECTOR AREAS
  ============================================================ */
  
  .connector-area {
    position: relative;
  
    width: 100%;
    overflow: hidden;
  }
  
  .connector-area svg {
    display: block;
    width: 100%;
    height: 100%;
  
    overflow: visible;
  }
  
  .connector-executive-manager {
    height: 112px;
  }
  
  .connector-manager-staff {
    height: 136px;
  }
  
  .connector-extra {
    height: 83px;
  }
  
  /* ============================================================
     CONNECTION LINES
  ============================================================ */
  
  .connection-main {
    stroke: #68a1ed;
    stroke-width: 1.7;
    stroke-opacity: 0.62;
  
    fill: none;
  }
  
  .connection-stem {
    stroke: #7dacec;
    stroke-width: 1.25;
    stroke-opacity: 0.33;
  }
  
  .connection-highlight {
    stroke: #3683ef;
    stroke-width: 2.2;
    stroke-opacity: 0.82;
  }
  
  /* ============================================================
     CONNECTION DOTS
  ============================================================ */
  
  .branch-junction {
    fill: #69a2eb;
  
    stroke: var(--page-bg);
    stroke-width: 2;
  }
  
  .branch-end {
    fill: #367fdf;
  
    stroke: var(--page-bg);
    stroke-width: 2.7;
  }
  
  .connector-center {
    fill: #337fe8;
  
    stroke: var(--page-bg);
    stroke-width: 3;
  }
  
  .connector-pulse {
    animation:
      junctionGlow 2.6s ease-in-out infinite;
  }
  
  /* ============================================================
     EMPLOYEE GRID
  ============================================================ */
  
  .node-grid {
    display: grid;
  
    grid-template-columns:
      repeat(var(--columns), minmax(0, 1fr));
  
    gap: 11px;
  
    align-items: stretch;
  }
  
  .structure-node-slot {
    min-width: 0;
  
    width: 100%;
    height: 100%;
  
    animation:
      structureNodeAppear 0.45s ease both;
  
    animation-delay:
      var(--node-delay, 0ms);
  }
  
  /* ============================================================
     STAFF ROWS
  ============================================================ */
  
  .staff-rows {
    display: flex;
    flex-direction: column;
  }
  
  .staff-row-block {
    position: relative;
  }
  
  /* ============================================================
     TREE ENDING
  ============================================================ */
  
  .tree-ending {
    display: flex;
    flex-direction: column;
    align-items: center;
  
    margin-top: 0;
  }
  
  .ending-line {
    width: 2px;
    height: 38px;
  
    background:
      linear-gradient(
        rgba(62, 137, 239, 0.50),
        transparent
      );
  }
  
  .ending-dot {
    width: 8px;
    height: 8px;
  
    background: #87aeec;
  
    border: 3px solid var(--page-bg);
    border-radius: 50%;
  
    box-sizing: content-box;
  }
  
  .tree-ending > span {
    margin-top: 9px;
  
    color: var(--subtle);
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 9px;
  
    letter-spacing: 1.6px;
    text-align: center;
  }
  
  /* ============================================================
     INFORMATION NOTE
  ============================================================ */
  
  .structure-note {
    display: flex;
    align-items: flex-start;
  
    gap: 12px;
  
    max-width: 850px;
  
    margin: 23px auto 0;
    padding: 15px 17px;
  
    background: var(--blue-soft);
  
    border: 1px solid var(--border);
    border-radius: 13px;
  }
  
  .note-icon {
    width: 19px;
    height: 19px;
  
    flex-shrink: 0;
    color: var(--blue);
  }
  
  .note-icon svg {
    width: 100%;
    height: 100%;
  }
  
  .structure-note p {
    margin: 0;
  
    color: var(--muted);
  
    font-size: 10px;
    line-height: 1.7;
  }
  
  /* ============================================================
     LOADING
  ============================================================ */
  
  .loading-state {
    display: flex;
    flex-direction: column;
  
    gap: 22px;
  }
  
  .skeleton {
    background:
      linear-gradient(
        90deg,
        var(--surface-soft) 20%,
        var(--surface) 45%,
        var(--surface-soft) 70%
      );
  
    background-size: 220% 100%;
  
    animation:
      shimmer 1.45s linear infinite;
  }
  
  .skeleton-banner {
    height: 185px;
    border-radius: 22px;
  }
  
  .skeleton-title {
    width: 320px;
    max-width: 100%;
  
    height: 25px;
    border-radius: 8px;
  }
  
  .skeleton-executive {
    width: 265px;
    height: 265px;
  
    align-self: center;
  
    border-radius: 23px;
  }
  
  .skeleton-branches {
    height: 75px;
    border-radius: 12px;
  }
  
  .skeleton-row {
    display: flex;
  
    gap: 10px;
    overflow: hidden;
  }
  
  .skeleton-avatar {
    flex: 1;
  
    min-width: 90px;
    height: 170px;
  
    border-radius: 16px;
  }
  
  .loading-caption {
    text-align: center;
  
    color: var(--muted);
    font-size: 11px;
  }
  
  /* ============================================================
     ERROR
  ============================================================ */
  
  .error-state {
    max-width: 560px;
  
    display: flex;
    flex-direction: column;
    align-items: center;
  
    margin: 65px auto;
    padding: 50px 30px;
  
    background: var(--surface);
  
    border: 1px solid var(--border);
    border-radius: 22px;
  
    text-align: center;
  }
  
  .error-symbol {
    width: 60px;
    height: 60px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    background: rgba(220, 93, 93, 0.1);
    color: #d67070;
  
    border-radius: 18px;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 25px;
  }
  
  .error-state h2 {
    margin: 19px 0 0;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 20px;
  }
  
  .error-state p {
    color: var(--muted);
  
    font-size: 12px;
    line-height: 1.7;
  }
  
  .error-state button {
    margin-top: 16px;
    padding: 12px 18px;
  
    border: none;
    border-radius: 10px;
  
    background: #2173e5;
    color: #ffffff;
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 11px;
  
    cursor: pointer;
  }
  
  /* ============================================================
     ANIMATIONS
  ============================================================ */
  
  @keyframes shimmer {
    to {
      background-position-x: -220%;
    }
  }
  
  @keyframes structureNodeAppear {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
  
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes junctionGlow {
    0%,
    100% {
      opacity: 0.65;
    }
  
    50% {
      opacity: 1;
    }
  }
  
  /* ============================================================
     TABLET
  ============================================================ */
  
  @media (max-width: 1100px) {
    .section-intro {
      align-items: flex-start;
      flex-direction: column;
    }
  
    .toolbar {
      align-items: stretch;
    }
  
    .search-box {
      flex-basis: 100%;
    }
  
    .filter-tabs {
      overflow-x: auto;
      flex-wrap: nowrap;
    }
  }
  
  /* ============================================================
     MOBILE
  ============================================================ */
  
  @media (max-width: 700px) {
    .page-container {
      width: calc(100% - 24px);
  
      padding-top: 90px;
      padding-bottom: 120px;
    }
  
    .hero {
      padding: 22px 19px 20px;
      border-radius: 19px;
    }
  
    .hero-logo {
      width: 51px;
      height: 51px;
  
      border-radius: 14px;
    }
  
    .hero-identity {
      gap: 11px;
    }
  
    .hero-eyebrow {
      font-size: 8px;
      letter-spacing: 0.7px;
    }
  
    .hero-copy h1 {
      font-size: 19px;
    }
  
    .hero-copy p {
      font-size: 11px;
    }
  
    .hero-refresh {
      width: 31px;
      height: 31px;
    }
  
    .hero-stats {
      display: grid;
  
      grid-template-columns:
        repeat(2, minmax(0, 1fr));
  
      gap: 18px 12px;
    }
  
    .stat-separator {
      display: none;
    }
  
    .hero-stat strong {
      font-size: 23px;
    }
  
    .section-intro {
      margin-top: 30px;
    }
  
    .section-intro h2 {
      font-size: 21px;
    }
  
    .toolbar {
      margin-top: 19px;
      padding: 10px;
    }
  
    .search-box {
      min-width: 0;
    }
  
    .filter-tab {
      flex-shrink: 0;
    }
  
    .tree-heading {
      margin-top: 27px;
    }
  
    .mobile-scroll-hint {
      display: flex;
      align-items: center;
  
      gap: 7px;
  
      margin: 13px 0 0;
  
      color: var(--muted);
  
      font-size: 10px;
    }
  
    .mobile-scroll-hint svg {
      width: 17px;
      height: 17px;
      flex-shrink: 0;
    }
  
    .tree-scroll {
      margin-top: 3px;
      padding-bottom: 15px;
  
      -webkit-overflow-scrolling: touch;
    }
  
    .org-canvas {
      padding-left: 14px;
      padding-right: 14px;
    }
  
    .structure-note {
      margin-top: 15px;
    }
  }
  
  /* ============================================================
     REDUCED MOTION
  ============================================================ */
  
  @media (prefers-reduced-motion: reduce) {
    .structure-node-slot,
    .skeleton,
    .connector-pulse {
      animation: none;
    }
  
    .hero-refresh {
      transition: none;
    }
  }


/* ============================================================
   CEO → MANAGEMENT
   CURVED PREMIUM CONNECTIONS
============================================================ */

.connector-executive-manager {
  position: relative;

  height: 150px;

  overflow: visible;

  margin: 0;
}

/* ============================================================
   CENTRAL TRUNK
============================================================ */

.ceo-flow-trunk {
  fill: none;

  stroke: #CBA66C;
  stroke-width: 2.5;

  stroke-linecap: round;

  opacity: 0.85;
}

/* ============================================================
   ROOT HALO
============================================================ */

.ceo-flow-halo {
  fill: rgba(63, 143, 236, 0.09);

  stroke: rgba(81, 149, 233, 0.19);
  stroke-width: 1;

  animation: ceoHaloPulse 3s ease-in-out infinite;

  transform-box: fill-box;
  transform-origin: center;
}

/* ============================================================
   CENTRAL NODE
============================================================ */

.ceo-flow-root {
  fill: var(--page-bg);

  stroke: #408BEA;
  stroke-width: 2.5;
}

.ceo-flow-root-center {
  fill: #408BEA;

  filter:
    drop-shadow(0 0 5px rgba(64, 139, 234, 0.65));
}

/* ============================================================
   CURVED BRANCHES
============================================================ */

.ceo-flow-branch {
  fill: none;

  stroke: url(#ceoFlowGradient);
  stroke-width: 1.8;

  stroke-linecap: round;
  stroke-linejoin: round;

  opacity: 0.82;

  filter:
    drop-shadow(0 1px 2px rgba(53, 132, 232, 0.12));
}

/* ============================================================
   MOVING LIGHT PARTICLES
============================================================ */

.ceo-flow-particles {
  fill: none;

  stroke: #8CC5FF;
  stroke-width: 2.5;

  stroke-linecap: round;

  stroke-dasharray: 3 26;
  stroke-dashoffset: 80;

  opacity: 0.55;

  animation:
    ceoFlowTravel 4.5s linear infinite;
}

/* ============================================================
   ARROWHEADS
============================================================ */

.ceo-flow-arrow {
  fill: none;

  stroke: #579CEB;
  stroke-width: 2.5;

  stroke-linecap: round;
  stroke-linejoin: round;

  opacity: 0.92;

  filter:
    drop-shadow(0 0 3px rgba(70, 146, 239, 0.18));
}

/* ============================================================
   DARK THEME
============================================================ */

.structure-page.dark .ceo-flow-root {
  fill: var(--page-bg);
  stroke: #83B6FF;
}

.structure-page.dark .ceo-flow-arrow {
  stroke: #9BC9FF;
}

.structure-page.dark .ceo-flow-particles {
  stroke: #B6DAFF;
  opacity: 0.65;
}

/* ============================================================
   ANIMATIONS
============================================================ */

@keyframes ceoHaloPulse {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.88);
  }

  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

@keyframes ceoFlowTravel {
  from {
    stroke-dashoffset: 80;
  }

  to {
    stroke-dashoffset: 0;
  }
}

/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {
  .ceo-flow-halo,
  .ceo-flow-particles {
    animation: none;
  }

  .ceo-flow-particles {
    opacity: 0.15;
  }
}


</style>
  